import { createContext, useState, useEffect, useContext } from "react";
import { Get, handleError } from "../utils/fetch.util";
import { UserContext } from "../contexts/user.context";
import { Sector } from "../types/company.types";
import { Dispatch, SetStateAction } from "react";
import { Contract } from "../types/company.types";
import { AssetType } from "../routes/dashboard/assetTypes/assetTypes.route";
import { useLocation, useSearchParams } from "react-router-dom";
type ProviderProps = {
  children: React.ReactNode;
};
type CommanData = {
  getSegments: (client_id?: number) => Promise<void>;
  segments: any[];
  setSegments: Dispatch<SetStateAction<any[]>>;
  getSectors: (client_id?: number) => Promise<void>;
  sectors: Sector[];
  options: any[];
  contracts: Contract[];
  assetTypes: AssetType[];
  setAssetTypes: Dispatch<SetStateAction<AssetType[]>>;
  setSectors: Dispatch<SetStateAction<Sector[]>>;
  setOptions: Dispatch<SetStateAction<any[]>>;
  isFetchingSectors: boolean;
  isFetchingSegments: boolean;
  API_LINK: string;
  recycleBin: string;
  setRecycleBin: Dispatch<SetStateAction<string>>;
};
const CommanDataContext = createContext<CommanData>({
  getSegments: async (client_id?: number) => { },
  segments: [],
  setSegments: (segment: any) => {
    return segment;
  },
  getSectors: async (client_id?: number) => { },
  sectors: [],
  contracts: [],
  setSectors: (sector: any) => {
    return sector;
  },
  assetTypes: [],
  setAssetTypes: (assetType: any) => {
    return assetType;
  },
  isFetchingSectors: true,
  isFetchingSegments: true,
  API_LINK: "",
  options: [],
  setOptions: (options: any) => {
    return options;
  },
  recycleBin: "",
  setRecycleBin: (recycleBin: any) => {
    return recycleBin;
  },
});
const CommanDataProvider = ({ children }: ProviderProps) => {
  const [segments, setSegments] = useState<any[]>([]);
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [options, setOptions] = useState<any[]>([]);
  const [recycleBin, setRecycleBin] = useState<string>("");
  const [assetTypes, setAssetTypes] = useState<AssetType[]>([]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const { getValidToken, isUser } = useContext(UserContext);
  const [isFetchingSectors, setIsFetchingSectors] = useState<boolean>(true);
  const [isFetchingSegments, setIsFetchingSegments] = useState<boolean>(true);
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const API_LINK: string = import.meta.env.VITE_API_URL;

  const getSegments = async (client_id?: number) => {
    setIsFetchingSegments(true);
    const response = await Get(location.pathname.includes("/admin/segments") ? `asdc-client-segment/get-all-segments?client_id=${client_id}` : "asdc-client-segment/client-segments", getValidToken());
    if (response?.error || !response?.data) {
      handleError(response);
      setIsFetchingSegments(false);
      return;
    }
    setSegments(response.data);
    setIsFetchingSegments(false);
  };
  const getSectors = async (client_id?: number) => {
    setIsFetchingSectors(true);
    const response = await Get(
      location.pathname.includes("/admin/segments") ? `asdc-client-sector/get-all-sectors?asdc_client_id=${client_id}` : "asdc-client-sector/client-sectors",
      getValidToken(),
    );

    if (response?.error || !response?.data) {
      handleError(response);
      setIsFetchingSectors(false);
      return;
    }

    setSectors(response.data);
    setIsFetchingSectors(false);
  };


  useEffect(() => {
    if (!isUser) return;

    const getContracts = async () => {
      const response = await Get(
        "asdc-client-contract/client-contracts-for-client-user",
        getValidToken(),
      );
      if (response?.error || !response?.data) {
        handleError(response);
      } else {
        setContracts(response?.data);
      }
    };

    const getAllAssetsTypes = async () => {
      const res = await Get("assets/get-all-asset-types", getValidToken());

      if (res.error || !res.data) {
        return;
      }

      setAssetTypes(res.data);
    };

    getAllAssetsTypes();
    getSectors(Number(searchParams.get("client_id")));
    getSegments(Number(searchParams.get("client_id")));
    getContracts();
  }, [isUser, getValidToken]);

  return (
    <CommanDataContext.Provider
      value={{
        getSegments,
        segments,
        setSegments,
        getSectors,
        sectors,
        setSectors,
        contracts,
        isFetchingSectors,
        isFetchingSegments,
        API_LINK,
        assetTypes,
        setAssetTypes,
        options,
        setOptions,
        recycleBin,
        setRecycleBin,
      }}
    >
      {children}
    </CommanDataContext.Provider>
  );
};
const useCommanData = () => useContext(CommanDataContext);
export { CommanDataProvider, useCommanData };
